import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Edit3, Trash2, Eye, EyeOff, Save, X } from 'lucide-react';
import {
  listTestimonials, createTestimonial, updateTestimonial, deleteTestimonial, reorderTestimonials,
} from './api';
import SortableClientGrid from './SortableClientGrid';
import { TabHeader, EmptyState, SkeletonRows, Label } from './AdminDashboard';

const FULL_MSG = 'Three quotes are already shown on the Home page. Hide one before showing another.';
const EMPTY = { quote: '', name: '', role: 'Client', show_on_home: true };
const inputCls = 'mt-2 w-full bg-[#faf8f4] rounded-lg border border-black/10 focus:border-[#002b5c] py-3 px-4 outline-none text-sm';

function QuoteEditor({ initial, onCancel, onSave }) {
  const [form, setForm] = useState({ ...EMPTY, ...initial });
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <form
      data-testid="quote-editor"
      onSubmit={(e) => { e.preventDefault(); onSave({ quote: form.quote, name: form.name, role: form.role, show_on_home: form.show_on_home }); }}
      className="bg-white border border-black/8 rounded-2xl p-6 lg:p-8 max-w-3xl space-y-6"
    >
      <div>
        <Label>Quote</Label>
        <textarea data-testid="quote-text-input" required rows={5} value={form.quote} onChange={(e) => upd('quote', e.target.value)} className={inputCls} />
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <Label>Name</Label>
          <input data-testid="quote-name-input" required value={form.name} onChange={(e) => upd('name', e.target.value)} className={inputCls} />
        </div>
        <div>
          <Label>Role</Label>
          <input data-testid="quote-role-input" value={form.role} onChange={(e) => upd('role', e.target.value)} className={inputCls} />
        </div>
      </div>
      <label className="flex items-center gap-3 cursor-pointer">
        <input data-testid="quote-show-checkbox" type="checkbox" checked={!!form.show_on_home} onChange={(e) => upd('show_on_home', e.target.checked)} className="w-4 h-4 accent-[#002b5c]" />
        <span className="text-sm text-[#333]">Show on the Home page (up to three quotes)</span>
      </label>
      <div className="flex gap-2">
        <button type="submit" data-testid="quote-save" className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase bg-[#002b5c] text-white px-5 py-2.5 rounded-full hover:bg-[#001a38] transition-colors">
          <Save size={13} /> Save
        </button>
        <button type="button" onClick={onCancel} data-testid="quote-cancel" className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#002b5c] px-5 py-2.5 rounded-full border border-black/15 hover:border-[#002b5c] transition-colors">
          <X size={13} /> Cancel
        </button>
      </div>
    </form>
  );
}

function QuoteCard({ t, handle, onToggle, onEdit, onDelete }) {
  return (
    <div data-testid={`quote-card-${t.id}`} className={`bg-white border rounded-xl p-5 h-full flex gap-3 transition-colors ${t.show_on_home ? 'border-[#C5A059]/50' : 'border-black/8 opacity-70'}`}>
      <div className="pt-1">{handle}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-[#333] leading-relaxed line-clamp-4">"{t.quote}"</p>
        <p className="font-heading uppercase text-[#002b5c] mt-3">{t.name}</p>
        <p className="text-[10px] tracking-[0.22em] uppercase text-[#6b6b6b]">{t.role}</p>
        <button
          type="button"
          onClick={onToggle}
          data-testid={`quote-show-toggle-${t.id}`}
          aria-pressed={!!t.show_on_home}
          className={`mt-3 inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase rounded-full px-2.5 py-1 border transition-colors ${t.show_on_home ? 'bg-[#C5A059] border-[#C5A059] text-white' : 'border-black/15 text-[#6b6b6b] hover:border-[#C5A059] hover:text-[#C5A059]'}`}
        >
          {t.show_on_home ? <Eye size={11} /> : <EyeOff size={11} />}
          {t.show_on_home ? 'On Home page' : 'Hidden'}
        </button>
      </div>
      <div className="flex flex-col gap-1.5">
        <button onClick={onEdit} data-testid={`quote-edit-${t.id}`} aria-label="Edit" className="p-2 rounded-full border border-black/15 hover:border-[#002b5c] text-[#002b5c] transition-colors"><Edit3 size={14} /></button>
        <button onClick={onDelete} data-testid={`quote-delete-${t.id}`} aria-label="Delete" className="p-2 rounded-full border border-black/15 hover:border-red-500 hover:text-red-500 text-[#6b6b6b] transition-colors"><Trash2 size={14} /></button>
      </div>
    </div>
  );
}

export default function TestimonialsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const load = () => { setLoading(true); listTestimonials().then((r) => { setRows(r); setLoading(false); }).catch(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  const shown = rows.filter((r) => r.show_on_home).length;
  const err = (e, fb) => toast.error(e?.response?.data?.detail || fb);

  if (editing) {
    return (
      <QuoteEditor
        initial={editing === 'new' ? { show_on_home: shown < 3 } : rows.find((r) => r.id === editing)}
        onCancel={() => setEditing(null)}
        onSave={async (v) => {
          try {
            if (editing === 'new') await createTestimonial(v); else await updateTestimonial(editing, v);
            toast.success('Saved.'); setEditing(null); load();
          } catch (e) { err(e, 'Save failed'); }
        }}
      />
    );
  }

  const toggle = async (t) => {
    if (!t.show_on_home && shown >= 3) { toast.error(FULL_MSG); return; }
    try {
      await updateTestimonial(t.id, { quote: t.quote, name: t.name, role: t.role, show_on_home: !t.show_on_home });
      setRows((prev) => prev.map((x) => (x.id === t.id ? { ...x, show_on_home: !t.show_on_home } : x)));
    } catch (e) { err(e, 'Could not update'); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete the quote from ${t.name}? This cannot be undone.`)) return;
    try { await deleteTestimonial(t.id); toast.success('Deleted.'); load(); } catch (e) { err(e, 'Delete failed'); }
  };
  const reorder = async (next) => {
    setRows(next);
    try { await reorderTestimonials(next.map((r) => r.id)); toast.success('Order saved.'); } catch (e) { err(e, 'Could not save order'); load(); }
  };

  return (
    <section>
      <TabHeader title="Client quotes" count={rows.length} onRefresh={load} onNew={() => setEditing('new')} newLabel="New quote" />
      <p data-testid="quotes-shown-count" className="mb-6 text-xs text-[#6b6b6b]">
        <span className="text-[#002b5c]">{shown} of 3 shown on the Home page.</span> Drag to set their order.
      </p>
      {loading ? <SkeletonRows /> : rows.length === 0 ? <EmptyState label="No quotes yet." /> : (
        <SortableClientGrid
          rows={rows}
          onReorder={reorder}
          testIdPrefix="quote"
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3"
          renderItem={(t, handle) => (
            <QuoteCard t={t} handle={handle} onToggle={() => toggle(t)} onEdit={() => setEditing(t.id)} onDelete={() => remove(t)} />
          )}
        />
      )}
    </section>
  );
}
